import { useEffect, useState } from 'react';
import { fetchCollection, getVal, submitWebform } from '../lib/api';
import ScrollReveal from '../components/ScrollReveal';

export default function Contact() {
  const [pageData, setPageData] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    async function loadContactCms() {
      try {
        const [pageRes, contactRes] = await Promise.all([
          fetchCollection('sider', { term: 'sidetype:kontakt' }),
          fetchCollection('kontakt_information'),
        ]);

        if (pageRes.items && pageRes.items.length > 0) {
          setPageData(pageRes.items[0]);
        }
        if (contactRes.items && contactRes.items.length > 0) {
          setContactInfo(contactRes.items[0]);
        }
      } catch (err) {
        console.error('Fejl ved hentning af kontakt-CMS data:', err);
      }
    }
    loadContactCms();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    setSubmitError('');

    if (!name.trim() || !phone.trim() || !email.trim() || !message.trim()) {
      setSubmitStatus('error');
      setSubmitError('Venligst udfyld alle påkrævede felter.');
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        name,
        phone,
        email,
        message,
        website_url: websiteUrl,
      };

      await submitWebform(payload);
      setSubmitStatus('success');
      setName('');
      setPhone('');
      setEmail('');
      setMessage('');
    } catch (err) {
      console.error('Fejl ved indsendelse af kontaktformular:', err);
      setSubmitStatus('error');
      setSubmitError(err.message || 'Der skete en fejl ved indsendelse af formularen. Prøv venligst igen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-stone-50">
      <section className="border-b border-stone-200 bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <ScrollReveal animation="fade-in-down">
            <span className="text-xs font-bold uppercase tracking-[0.35em] text-brand-orange">Kontakt</span>
            <h1 className="mt-3 text-4xl font-semibold text-slate-800 sm:text-5xl">
              {getVal(pageData?.title) || 'Kontakt os'}
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-slate-500 sm:text-base">
              {getVal(pageData?.subtitle) || 'Har du spørgsmål til vores maskiner, priser eller ønsker du at leje udstyr? Vi sidder klar til at hjælpe dig hurtigt videre med dit projekt.'}
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="flex-grow py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            <ScrollReveal animation="fade-in-left" className="flex flex-col justify-between space-y-8">
              <div>
                <h2 className="mb-6 text-2xl font-semibold text-slate-800">Kontaktoplysninger</h2>
                <p className="mb-8 leading-relaxed text-slate-500">
                  Du er altid velkommen til at kontakte os via telefon eller e-mail. Vi svarer så hurtigt som muligt, ofte inden for få timer.
                </p>

                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row items-start gap-4 rounded-[1.25rem] border border-stone-200 bg-white p-5 shadow-[0_12px_30px_rgba(148,163,184,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange">
                      <i className="fa-solid fa-location-dot text-lg" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400">Adresse</h3>
                      <p className="mt-1 font-semibold text-slate-800">Søllingevej 35</p>
                      <p className="text-slate-500">5750 Ringe</p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-start gap-4 rounded-[1.25rem] border border-stone-200 bg-white p-5 shadow-[0_12px_30px_rgba(148,163,184,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange">
                      <i className="fa-solid fa-phone text-lg" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400">Telefon</h3>
                      <p className="mt-1">
                        <a href="tel:22426011" className="font-semibold text-slate-800 transition-colors hover:text-brand-orange whitespace-nowrap">
                          22 42 60 11
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-start gap-4 rounded-[1.25rem] border border-stone-200 bg-white p-5 shadow-[0_12px_30px_rgba(148,163,184,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange">
                      <i className="fa-solid fa-envelope text-lg" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400">E-mail</h3>
                      <p className="mt-1">
                        <a href="mailto:J_danielsen85@hotmail.com" className="font-semibold text-slate-800 transition-colors hover:text-brand-orange whitespace-nowrap">
                          J_danielsen85@hotmail.com
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-start gap-4 rounded-[1.25rem] border border-stone-200 bg-white p-5 shadow-[0_12px_30px_rgba(148,163,184,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange">
                      <i className="fa-solid fa-file-invoice text-lg" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400">CVR-nummer</h3>
                      <p className="mt-1 font-semibold text-slate-800">44703548</p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal animation="fade-in-right" className="rounded-[1.5rem] border border-stone-200 bg-white p-8 shadow-[0_18px_45px_rgba(148,163,184,0.12)]">
              <h2 className="mb-6 text-2xl font-semibold text-slate-800">Send os en besked</h2>

              {submitStatus === 'success' && (
                <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-700">
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-check text-xl" />
                    <div>
                      <p className="font-bold">Tak for din henvendelse!</p>
                      <p className="mt-1 text-sm">Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.</p>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-700">
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-exclamation text-xl" />
                    <div>
                      <p className="font-bold">Der skete en fejl</p>
                      <p className="mt-1 text-sm">{submitError}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="sr-only absolute overflow-hidden w-0 h-0" aria-hidden="true">
                  <label htmlFor="website_url">Do not fill this field</label>
                  <input
                    id="website_url"
                    type="text"
                    name="website_url"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label htmlFor="name" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Navn <span className="text-brand-orange">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Dit fulde navn"
                    className="w-full rounded-2xl border border-stone-200 bg-white px-4 py-3 text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-brand-orange"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Telefonnummer <span className="text-brand-orange">*</span>
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Dit telefonnummer"
                    className="w-full rounded-2xl border border-stone-200 bg-white px-4 py-3 text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-brand-orange"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    E-mailadresse <span className="text-brand-orange">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="din.email@eksempel.dk"
                    className="w-full rounded-2xl border border-stone-200 bg-white px-4 py-3 text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-brand-orange"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Beskrivelse / Besked <span className="text-brand-orange">*</span>
                  </label>
                  <textarea
                    id="message"
                    required
                    rows="5"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Skriv hvad du ønsker at leje, lejeperiode eller andre spørgsmål..."
                    className="w-full resize-none rounded-2xl border border-stone-200 bg-white px-4 py-3 text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-brand-orange"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-full bg-brand-orange py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-orange-hover disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="fa-solid fa-spinner animate-spin" />
                      Sender besked...
                    </span>
                  ) : (
                    'Send besked'
                  )}
                </button>
              </form>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Full-width Google Maps Section */}
      <section className="w-full overflow-hidden border-t border-stone-200 bg-stone-100">
        <div className="relative w-full h-[250px] md:h-[400px]">
          <iframe
            title="JFD Service Google Maps"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d154460.37007476413!2d10.376889218553206!3d55.277716636418916!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464d25786fce00a9%3A0x49bf54e4b13f5844!2sJFD%20service!5e0!3m2!1sen!2sdk!4v1782395847933!5m2!1sen!2sdk"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            className="w-full h-full grayscale hover:grayscale-0 transition-all duration-500 ease-in-out"
          />
        </div>
      </section>
    </div>
  );
}
